import React, { useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { useMutation } from '@apollo/client'
import { useClipboard } from 'use-clipboard-copy'
import gql from 'graphql-tag'

import { FormRow } from '../../components/common/form/Form'
import { notification } from '../../components/common/notifications/Notifications'
import { ButtonLight, Input } from 'front-end/src/components'
import IconDocs from 'front-end/src/components/common/icon/IconDocs'

export interface IGenerateResetPasswordLinkVariables {
  userId: number
}

const generateResetPasswordLinkMutation = gql`
  mutation ($userId: PositiveInt!) {
    admin {
      user(id: $userId) {
        id
        generatePasswordResetLink
      }
    }
  }
`

const sendPasswordResetEmailMutation = gql`
  mutation ($userId: PositiveInt!) {
    admin {
      user(id: $userId) {
        id
        sendPasswordResetEmail
      }
    }
  }
`

export const UserPasswordReset: React.FC<{
  userId: number
  userEmail?: string | null
}> = ({ userId, userEmail }) => {
  const [
    generateResetPasswordLink,
    { loading: generateResetPasswordLinkLoading }
  ] = useMutation<
    {
      admin: {
        user: {
          generatePasswordResetLink: string
        }
      }
    },
    IGenerateResetPasswordLinkVariables
  >(generateResetPasswordLinkMutation)

  const [sendPasswordResetEmail, { loading: sendPasswordResetEmailLoading }] =
    useMutation<
      {
        admin: {
          user: {
            sendPasswordResetEmail: number
          }
        }
      },
      IGenerateResetPasswordLinkVariables
    >(sendPasswordResetEmailMutation)

  const [generatedResetLink, setGeneratedResetLink] = useState<null | string>(
    null
  )
  const [isReset, setIsReset] = useState(false)
  const clipboard = useClipboard()

  return (
    <div className="User__reset">
      <h3>
        <Trans>Reset password</Trans>
      </h3>

      <div>
        <p>
          <Trans>
            To force a reset of this user's password, please click the button
            below. This will trigger an email with a link for them to set a new
            password.
          </Trans>
        </p>
        <ButtonLight
          disabled={sendPasswordResetEmailLoading || isReset}
          isLoading={sendPasswordResetEmailLoading}
          onClick={async () => {
            if (!userEmail) {
              notification.error(
                t`This user doesn't have an email set so we can't reset the password via email.`
              )
              return
            }

            const { data } = await sendPasswordResetEmail({
              variables: {
                userId
              }
            })

            if (data && data.admin.user.sendPasswordResetEmail) {
              notification.info(t`Email sent successfully `)
              setIsReset(true)
            } else {
              notification.error(t`Operation failed`)
            }
          }}
        >
          <Trans>Reset password</Trans>
        </ButtonLight>
      </div>
      <br />
      <p>
        <Trans>If you prefer to send the link yourself</Trans>
      </p>

      {!generatedResetLink && (
        <div>
          <ButtonLight
            isDisabled={generateResetPasswordLinkLoading}
            isLoading={generateResetPasswordLinkLoading}
            onClick={async () => {
              const { data } = await generateResetPasswordLink({
                variables: {
                  userId
                }
              })

              if (data && data.admin.user.generatePasswordResetLink) {
                setGeneratedResetLink(data.admin.user.generatePasswordResetLink)
              } else {
                notification.error(t`Operation failed`)
              }
            }}
          >
            <Trans>Generate password reset link</Trans>
          </ButtonLight>
        </div>
      )}
      {generatedResetLink && (
        <FormRow className="User__reset__send">
          <Input value={generatedResetLink} isReadOnly={true} />
          <ButtonLight
            onClick={() => {
              clipboard.copy(generatedResetLink)
              notification.info(t`Link has been copied`)
            }}
          >
            <IconDocs mr="8px" />
            <Trans>Copy to clipboard</Trans>
          </ButtonLight>
        </FormRow>
      )}
    </div>
  )
}
